import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  SearchResultApiModel,
  FormInstanceDependenciesApiModel,
  CreateFormInstanceApiModel,
  ProcessPeFormStepApiModel,
  UserListApiModel,
  FormInstanceCollectionDeleteResultApiModel
} from '@app/api/api-models/';
import { ProcessPeFormStepResultApiModel } from '@app/api/api-models/pe-features/ProcessPeFormStepResultApiModel';
import { CheckDateRangeApiModel } from '@app/api/api-models/pe-features/CheckDateRangeApiModel';
import { CheckDateRangeResultApiModel } from '@app/api/api-models/pe-features/CheckDateRangeResultApiModel';
import { PpFormInstanceApiModel } from '@app/api/api-models/pp-form-instance/PpFormInstanceApiModel';
import { PpFormInstanceSelectorsApiModel } from '@app/api/api-models/pp-form-instance/PpFormInstanceSelectorsApiModel';
import { EndOfProgramPresequenceApiModel } from '@app/api/api-models/form-instance/EndOfProgramPresequenceApiModel';
import { FormInstanceSubjectNarrativeApiModel } from '@app/api/api-models/form-instance/FormInstanceSubjectNarrativeApiModel';
import { PpBoundFormInstanceApiModel } from '@app/api/api-models/pp-form-instance/PpBoundFormInstanceApiModel';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { sanitizeHtml } from '@shared/utils/editor-utils';

@Injectable()
export class PpFormInstanceService {
  public apiUrl = 'pp-form-instances';

  constructor(private readonly http: HttpClientService) {}

  public get(id: number): Observable<PpFormInstanceApiModel> {
    return this.http
      .get<PpFormInstanceApiModel>(`${this.apiUrl}/get/${id}`)
      .pipe(map((x) => new PpFormInstanceApiModel().deserialize(x)));
  }

  public getAvailableFormInstances(formInstanceId: number): Observable<Array<PpBoundFormInstanceApiModel>> {
    return this.http
      .get<Array<PpBoundFormInstanceApiModel>>(`${this.apiUrl}/${formInstanceId}/available`)
      .pipe(map((array) => array.map((x) => new PpBoundFormInstanceApiModel().deserialize(x))));
  }

  public add(model: CreateFormInstanceApiModel): Observable<PpFormInstanceApiModel> {
    return this.http.post<PpFormInstanceApiModel>(`${this.apiUrl}/add`, model);
  }

  public getNextUsers(id: number): Observable<Array<UserListApiModel>> {
    return this.http.get<Array<UserListApiModel>>(`${this.apiUrl}/${id}/next-users`);
  }

  public update(model: PpFormInstanceApiModel): Observable<PpFormInstanceApiModel> {
    model.CurrentComment = sanitizeHtml(model.CurrentComment);
    model.CreatorNarrative = sanitizeHtml(model.CreatorNarrative);
    return this.http.put<PpFormInstanceApiModel>(`${this.apiUrl}/update`, model);
  }

  public getSelectors(): Observable<PpFormInstanceSelectorsApiModel> {
    return this.http.get<PpFormInstanceSelectorsApiModel>(`${this.apiUrl}/selectors`);
  }

  public deleteById(id: number): Observable<Object> {
    return this.http.delete(`${this.apiUrl}/delete/${id}`);
  }

  public deleteMultiple(ids: Array<number>): Observable<FormInstanceCollectionDeleteResultApiModel> {
    let queryParams = new HttpParams();
    queryParams = queryParams.appendAll({ ids: ids });

    return this.http.delete(`${this.apiUrl}/delete-multiple`, { params: queryParams });
  }

  public processStep(model: ProcessPeFormStepApiModel): Observable<ProcessPeFormStepResultApiModel> {
    model.Comment = sanitizeHtml(model.Comment);

    return this.http.put<ProcessPeFormStepResultApiModel>(`${this.apiUrl}/process-step`, model);
  }

  public getSimilarNotSubmitted(
    formId: number,
    programRoleId: string | null = null,
    userId: string | null = null
  ): Observable<Array<SearchResultApiModel>> {
    const params = new HttpParams({
      fromObject: {
        formId: formId.toString(),
        toId: programRoleId ? programRoleId : '',
        toUserId: userId ? userId : ''
      }
    });

    return this.http.get<Array<SearchResultApiModel>>(`${this.apiUrl}/not-submitted`, { params: params });
  }

  public getEndOfProgramPresequences(
    programRoleId: string,
    formInstanceId?: number
  ): Observable<EndOfProgramPresequenceApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId,
        formInstanceId: formInstanceId || ''
      }
    });

    return this.http.get<EndOfProgramPresequenceApiModel>(`${this.apiUrl}/end-of-program-presequence`, {
      params: params
    });
  }

  public getSummariesById(formInstanceId: number): Observable<Array<SearchResultApiModel>> {
    const params = new HttpParams({
      fromObject: {
        formInstanceId: formInstanceId.toString()
      }
    });

    return this.http.get<Array<SearchResultApiModel>>(`${this.apiUrl}/summaries`, { params: params });
  }

  public getSummariesMultipleByIds(ids: Array<number>): Observable<Array<FormInstanceDependenciesApiModel>> {
    let queryParams = new HttpParams();
    queryParams = queryParams.appendAll({ ids: ids });

    return this.http.get<Array<FormInstanceDependenciesApiModel>>(`${this.apiUrl}/summaries-multiple`, {
      params: queryParams
    });
  }

  public checkUsedDateRange(model: CheckDateRangeApiModel): Observable<CheckDateRangeResultApiModel> {
    return this.http.post<CheckDateRangeResultApiModel>(`${this.apiUrl}/check-used-date-range`, model);
  }

  public updateSubjectNarrative(
    formInstanceId: number,
    narrative: FormInstanceSubjectNarrativeApiModel
  ): Observable<FormInstanceSubjectNarrativeApiModel> {
    return this.http.post<FormInstanceSubjectNarrativeApiModel>(
      `${this.apiUrl}/${formInstanceId}/update-subject-narrative`,
      narrative
    );
  }

  public getPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/pdf`, { responseType: 'blob' });
  }
}
